const securityPillars = [
  {
    icon: "ri-eye-off-line",
    title: "No-Log Architecture",
    desc: "Our infrastructure is engineered to never retain logs of user activity, DNS queries, or connection timestamps. Complete anonymity is guaranteed by design.",
  },
  {
    icon: "ri-server-line",
    title: "Encrypted Servers",
    desc: "All VPN endpoints are hosted on encrypted, RAM-only servers. No persistent storage means no residual data — even after a reboot.",
  },
  {
    icon: "ri-code-s-slash-line",
    title: "Open Audit Policy",
    desc: "Our security protocols undergo independent third-party audits regularly. Transparency is not optional — it is our commitment to every user.",
  },
  {
    icon: "ri-shield-flash-line",
    title: "Threat Intelligence",
    desc: "Real-time threat feeds and malware signature databases protect your device against emerging dangers as they are discovered globally.",
  },
];

export default function SecuritySection() {
  return (
    <section id="security" className="py-24 md:py-32 bg-gray-900 text-white overflow-hidden relative">
      {/* Decorative background element */}
      <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-emerald-500/5 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-rose-500/5 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 md:px-10 relative z-10">
        {/* Top row */}
        <div className="flex flex-col lg:flex-row lg:items-start gap-10 lg:gap-20 mb-16">
          {/* Left: text */}
          <div className="flex-1">
            <span className="inline-block text-xs font-semibold tracking-widest uppercase text-emerald-400 border border-emerald-400/30 px-3 py-1 rounded-full mb-6">
              Security Architecture
            </span>
            <h2 className="text-4xl md:text-6xl font-bold leading-tight mb-6">
              Your privacy<br />
              <span className="text-gray-500 font-light">is non-negotiable.</span>
            </h2>
            <p className="text-gray-400 text-base leading-relaxed max-w-lg">
              At FELIN SRL, security is not a feature — it is the foundation. Every line of code in our
              iOS applications is written with a security-first mindset, protecting millions of users from
              surveillance, data theft, and unauthorized access.
            </p>
            <div className="mt-8 border-t border-white/10 pt-8">
              <p className="text-sm text-gray-400 leading-relaxed">
                Our encryption standards exceed requirements set by industry bodies including
                <strong className="text-white"> ISO/IEC 27001</strong> and comply with
                <strong className="text-white"> GDPR</strong> regulations for all European users.
              </p>
            </div>
          </div>

          {/* Right: stats + image */}
          <div className="flex-1 flex flex-col gap-6">
            <div className="grid grid-cols-2 gap-4">
              {[
                { num: "0", label: "Data Breaches", color: "text-rose-400" },
                { num: "90+", label: "Server Locations", color: "text-amber-400" },
                { num: "100%", label: "Open Audit Passed", color: "text-sky-400" },
              ].map((stat) => (
                <div key={stat.label} className="bg-white/5 rounded-2xl p-5 border border-white/10">
                  <p className={`text-3xl font-bold ${stat.color}`}>{stat.num}</p>
                  <p className="text-gray-500 text-xs mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
            <div className="rounded-2xl overflow-hidden h-52">
              <img
                src="https://public.readdy.ai/ai/img_res/84be7ae11143124b1104a0a055c791dc.jpg"
                alt="Security infrastructure"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>

        {/* Security pillars */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {securityPillars.map((pillar) => (
            <div
              key={pillar.title}
              className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/8 transition-colors duration-200"
            >
              <div className="w-10 h-10 flex items-center justify-center bg-emerald-500/15 rounded-xl mb-4">
                <i className={`${pillar.icon} text-emerald-400 text-lg`} />
              </div>
              <h3 className="font-semibold text-white text-sm mb-2">{pillar.title}</h3>
              <p className="text-gray-500 text-xs leading-relaxed">{pillar.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
